import { BalanceType } from '../../../api/fetchers/userInfo/types';
import { useCoreSelector } from '../../CoreStore';

import * as rawSelectors from './rawSelectors';
import type { BetsSliceType, BetType, DragBufferType, IBetSpotValue, ILimit } from './types';

export const useBetSlice = {
  bets: (): BetType | undefined => {
    return useCoreSelector(rawSelectors.selectBets);
  },
  lastBet: (): BetsSliceType['lastBet'] => {
    return useCoreSelector(rawSelectors.selectLastBet);
  },
  betsReady: (): boolean => {
    return useCoreSelector(rawSelectors.selectBetsReady);
  },
  betList: (): BetsSliceType['betList'] => {
    return useCoreSelector(rawSelectors.selectBetList);
  },
  betLimits: (): BetsSliceType['betLimits'] => {
    return useCoreSelector(rawSelectors.selectBetLimits);
  },
  getMinBet: (): number => {
    return useCoreSelector(rawSelectors.selectMinBet);
  },
  rawTotalBet: (): number => {
    return useCoreSelector(rawSelectors.selectRawTotalBet);
  },
  getDisabledBetSpots: (): string[] => {
    return useCoreSelector(rawSelectors.selectDisabledBetSpots);
  },
  totalBet: (): number => {
    return useCoreSelector(rawSelectors.selectTotalBet);
  },
  hasTotalBet: (): boolean => {
    return useCoreSelector(rawSelectors.selectHasTotalBet);
  },
  acceptedBets: (): BetsSliceType['acceptedBets'] => {
    return useCoreSelector(rawSelectors.selectAcceptedBets);
  },
  /**
   * @returns string[] - Array of bet spots id's that are invalid based on min bet limits
   */
  invalidBetspots: (): string[] => {
    return useCoreSelector(rawSelectors.selectInvalidBetspots);
  },
  hasInvalidBets: (): boolean => {
    return useCoreSelector((state) => rawSelectors.selectInvalidBetspots(state).length > 0);
  },
  getDragBuffer: (): DragBufferType | null => {
    return useCoreSelector(rawSelectors.selectDragBuffer);
  },
  getPreliminaryDragBuffer: (): DragBufferType | null => {
    return useCoreSelector(rawSelectors.selectPreliminaryDragBuffer);
  },
  getIsDragging: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsDragging);
  },
  /**
   * Get timestamp of when was the last time betStack was updated
   */
  getUpdateTimestamp: (): number => {
    return useCoreSelector(rawSelectors.selectUpdateTimestamp);
  },
  getTableLimits: (): ILimit | null => {
    return useCoreSelector(rawSelectors.selectTableLimits);
  },
  getPlacedBetType: (): string => {
    return useCoreSelector(rawSelectors.selectPlacedBetType);
  },
  getMultiplierFactor: (): number | null => {
    return useCoreSelector(rawSelectors.selectMultiplierFactor);
  },
  isLowBalance: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsLowBalance);
  },
  isLowCompositeBalance: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsLowCompositeBalance);
  },
  isReBetEnabled: (): boolean => {
    return useCoreSelector(rawSelectors.selectReBetEnabled);
  },
  getBetSpot: (betSpot: number): IBetSpotValue | undefined => {
    return useCoreSelector((state) => state.bets.betStack?.[state.bets.betStack.length - 1]?.betSpots[betSpot]);
  },
  getCompositeBalance: (): BalanceType | undefined => {
    return useCoreSelector(rawSelectors.selectCompositeBalance);
  },
  getRebetAttempts: (): number => {
    return useCoreSelector((state) => state.bets.rebetAttempts);
  },
  getTotalBetForAutoplay: (): number => {
    return useCoreSelector(rawSelectors.selectTotalBetForAutoplay);
  },
};
